<script setup lang="ts">
import type {
  SoftwareDeviceType,
  SoftwareHomeDeviceData,
  SoftwareItem,
  SoftwarePlatformData,
  SoftwareRankingData,
} from '~/api/software'
import { isChineseLocale } from '~/composables/useAppI18n'
import { formatSoftwareNumber, selectSoftwareRankingList } from '~/utils/software'
import areaCodeCn from '~/assets/json/areaCodeCN.json'
import areaCodeEn from '~/assets/json/areaCodeEn.json'
import ArrowRight from '~/assets/icon/arrow-right.svg'

type CountryItem = {
  countryCode?: string
  name?: string
}

const POPULAR_USER_ICON =
  'https://resources1.interface003.com/web30/2026/09/10/f9681fc9-c6dd-4851-91e1-a1590a8a61d3.png'

const { locale, localePath, t } = useAppI18n()
const appStore = useAppStore()
const props = defineProps<{
  softwareHomeData?: SoftwarePlatformData<SoftwareHomeDeviceData>
  deviceType?: SoftwareDeviceType
}>()
const countryGroups = computed(() => (isChineseLocale(locale) ? areaCodeCn : areaCodeEn))
const countries = computed(() => countryGroups.value.flatMap((group) => group.data as CountryItem[]))
const currentCountry = computed(() => {
  return countries.value.find((country) => String(country.countryCode) === String(appStore.CountryCode))
})
const currentCountryName = computed(
  () => currentCountry.value?.name || (isChineseLocale(locale) ? '英国' : 'United Kingdom'),
)
const isRankingDialogVisible = ref(false)
/** 组件按接口返回的终端和国家完成热门应用筛选，页面只传递原始排行数据。 */
const popularList = computed(() => {
  const platformKey = (props.deviceType ?? 1) === 1 ? 'ios' : 'android'
  return selectSoftwareRankingList(props.softwareHomeData?.[platformKey]?.popular, 'current').slice(0, 7)
})
const popularRankingData = computed<SoftwarePlatformData<SoftwareRankingData>>(() => ({
  ios: props.softwareHomeData?.ios?.popular ?? { global: [], current: [] },
  android: props.softwareHomeData?.android?.popular ?? { global: [], current: [] },
}))

/** 仅在热门应用超过首屏展示数量时打开完整热门应用弹窗。 */
function handleOpenRankingDialog() {
  if (popularList.value.length > 6) isRankingDialogVisible.value = true
}

/** 关闭完整热门应用弹窗。 */
function handleCloseRankingDialog() {
  isRankingDialogVisible.value = false
}

/** 生成首页软件详情页路径，允许详情字段为空。 */
function buildHomeSoftwareDetailsPath(item: SoftwareItem) {
  const marketId = String(item.marketId ?? '').trim()
  const appBundleId = String(item.appBundleId ?? '').trim()
  const traderCode = String(item.traderCode ?? '').trim()
  const platformSuffix = marketId === '11' ? 2 : 1
  return `/crypto-apps/${marketId}-${appBundleId}-${traderCode}-${platformSuffix}?fromType=1`
}

/** 为热门应用完整榜单入口补齐 Enter 与 Space 键盘行为。 */
function handleKeyboardActivate(event: KeyboardEvent) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  ;(event.currentTarget as HTMLElement).click()
}
</script>

<template>
  <section v-if="popularList.length" id="local-popular-apps" class="w-full text-[#292a2e]">
    <span id="hot" class="block h-0 scroll-mt-[70px]" aria-hidden="true"></span>
    <div class="flex items-center justify-between">
      <div class="text-[24px] leading-[32px] font-semibold">
        {{ t('89332', ['{0}']).replace('{0}', currentCountryName) }}
      </div>
      <div
        v-if="popularList.length > 6"
        class="flex size-[32px] cursor-pointer items-center justify-center outline-none rtl:rotate-180 hover:[&_.store-arrow_path]:!fill-[#292A2E]"
        role="button"
        tabindex="0"
        :aria-label="t('storeHomeViewAll')"
        @click="handleOpenRankingDialog"
        @keydown="handleKeyboardActivate"
      >
        <ArrowRight class="store-arrow size-[20px]" aria-hidden="true" />
      </div>
    </div>

    <div class="mt-[20px] flex items-center overflow-x-auto overflow-y-hidden">
      <a
        v-for="(item, index) in popularList"
        :key="`${item.appBundleId}-${index}`"
        :href="localePath(buildHomeSoftwareDetailsPath(item))"
        target="_blank"
        rel="noopener noreferrer"
        class="popular-app-card flex w-[calc(100%/7)] min-w-[171px] shrink-0 cursor-pointer flex-col items-center rounded-[8px] px-[10px] py-[20px] hover:bg-[#F8F8F8]"
      >
        <CommonImage
          :src="item.logo"
          :alt="item.name"
          :width="76"
          :height="76"
          loading="eager"
          class="box-border shrink-0 rounded-[8px] border border-solid border-[rgba(5,21,36,0.06)]"
        />
        <div class="mt-[16px] flex w-full min-w-0 flex-col items-center">
          <div class="w-full truncate text-center text-[18px] leading-[26px] font-semibold" :title="item.name">
            {{ item.name }}
          </div>
          <div class="-mx-[2px] mt-[4px] flex items-center justify-center text-[14px] leading-[22px] text-[#7d818a]">
            <CommonImage :src="POPULAR_USER_ICON" alt="" :width="14" :height="14" loading="eager" class="mx-[2px]" />
            <span class="font-wiki-Medium mx-[2px]">{{ formatSoftwareNumber(item.activeValue) }}</span>
          </div>
        </div>
      </a>
    </div>

    <HomeSoftwareRankingDialog
      :visible="isRankingDialogVisible"
      ranking-type="popular"
      ranking-scope="current"
      :initial-device-type="deviceType ?? 1"
      :popular-data="popularRankingData"
      :country-name="currentCountryName"
      @close="handleCloseRankingDialog"
    />
  </section>
</template>

<style scoped lang="less"></style>
